import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import TimePicker from "@mui/lab/TimePicker";
import { Button, DialogActions } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import uploadImg from "../../../assets/images/uploadImg.svg";
import useEvents from "../../../hooks/useEvents";
import useInput from "../../../hooks/useInput";
import useReporting from "../../../hooks/useReporting";
import {
   convertDateToDDMMYYYY,
   convertDateToYYYYMMDD,
   splitEventTime,
} from "../../../utilities/helper";
import { validators } from "../../../utilities/inputValidator";
import FormDescription from "../formDescription/form-description";
import classes from "../FormDialog/formDialog.module.css";

const FormFill = () => {
   const [imgSelected, setImageSelected] = useState("");
   const [cloudiaryUrl, setCloudiaryUrl] = useState(null);
   const { reportingService } = useReporting();

   const {
      events: { event },
   } = useSelector((state) => state);
   const { closeEventsForm, cloudinaryApi, addEvent } = useEvents();

   const {
      value: eventTitle,
      valueInputChange: eventTitleInputChange,
      valueIsValid: titleIsValid,
      hasError: titleHasError,
      inputBlurHandler: titleBlurHandler,
   } = useInput(validators.checkValueIsEmpty, `${event.eventTitle ?? ""}`);
   const {
      value: eventDescription,
      setQuillValue: descruptionInputChange,
      valueIsValid: descriptionIsValid,
   } = useInput(
      validators.checkValueIsEmpty,
      `${event.eventDescription ?? ""}`
   );

   const {
      value: eventStartTime,
      valueInputChange: eventStartTimeInputChange,
      valueIsValid: eventStartTimeIsValid,
      hasError: eventStartTimeHasError,
      inputBlurHandler: eventStartTimeBlurHandler,
   } = useInput(
      validators.checkValueIsEmpty,
      `${splitEventTime(event.time)?.startTime}`
   );
   const {
      value: eventEndTime,
      valueInputChange: eventEndTimeInputChange,
      valueIsValid: eventEndTimeIsValid,
      hasError: eventEndTimeHasError,
      inputBlurHandler: eventEndTimeBlurHandler,
   } = useInput(
      validators.checkValueIsEmpty,
      `${splitEventTime(event.time)?.endTime}`
   );

   const {
      value: date,
      valueInputChange: dateInputChange,
      valueIsValid: dateIsValid,
      hasError: dateHasError,
      inputBlurHandler: dateBlurHandler,
   } = useInput(
      validators.checkValueIsEmpty,
      `${convertDateToYYYYMMDD(event.eventDate) ?? ""}`
   );
   const cloudUrl = cloudiaryUrl ?? event.eventImageUrl;

   const formIsValid =
      titleIsValid &&
      descriptionIsValid &&
      dateIsValid &&
      eventStartTimeIsValid &&
      eventEndTimeIsValid;

   const uploadToCloudiary = async () => {
      const data = await cloudinaryApi(imgSelected);
      setCloudiaryUrl(data.secure_url);
   };

   const onAddEvent = async () => {
      if (!formIsValid) {
         return reportingService("Form is invalid", "error");
      }

      const reqBody = {
         eventDate: convertDateToDDMMYYYY(date),
         eventDescription,
         eventTitle,
         eventImageUrl: cloudUrl,
         time: `${eventStartTime} - ${eventEndTime}`,
      };
      if (event.id) {
         reqBody.id = event.id;
      }

      addEvent(reqBody);
   };

   return (
      <>
         <DialogContent className={classes.dialog_height}>
            <div className={classes.formGroup}>
               <span>Event Title</span>
               <TextField
                  autoFocus
                  margin="dense"
                  id="name"
                  type="text"
                  multiline
                  rows={1}
                  fullWidth
                  variant="outlined"
                  value={eventTitle}
                  onChange={eventTitleInputChange}
                  onBlur={titleBlurHandler}
                  error={titleHasError}
               />
            </div>

            <div className={classes.formGroup}>
               <span>Event Description</span>
               <FormDescription
                  inputChange={descruptionInputChange}
                  value={eventDescription}
                  className={classes.FormDescription}
               />
            </div>
            <div className={classes.formGroup}>
               <span>Event Date</span>
               <TextField
                  margin="dense"
                  className={classes.textField}
                  error={dateHasError}
                  onBlur={dateBlurHandler}
                  value={date}
                  onChange={dateInputChange}
                  type="date"
                  fullWidth
               />
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
               <div className={classes.formGroup}>
                  <span>Event Start Time</span>
                  <TimePicker
                     className={classes.textField}
                     error={eventStartTimeHasError}
                     onBlur={eventStartTimeBlurHandler}
                     value={eventStartTime}
                     onChange={eventStartTimeInputChange}
                     fullWidth
                     margin="dense"
                     renderInput={(params) => (
                        <TextField {...params} fullWidth margin="dense" />
                     )}
                  />
               </div>

               <div className={classes.formGroup}>
                  <span>Event End Time</span>
                  <TimePicker
                     className={classes.textField}
                     error={eventEndTimeHasError}
                     onBlur={eventEndTimeBlurHandler}
                     value={eventEndTime}
                     onChange={eventEndTimeInputChange}
                     fullWidth
                     margin="dense"
                     renderInput={(params) => (
                        <TextField {...params} fullWidth margin="dense" />
                     )}
                  />
               </div>
            </LocalizationProvider>

            <div className={classes.uploadContainer}>
               <p>
                  Event Image: <span>{imgSelected.name} </span>{" "}
               </p>
               <span>
                  Cloudinary Url:{" "}
                  <span>{event.eventImageUrl ?? cloudiaryUrl}</span>
               </span>

               <div className={classes.upload}>
                  <img src={uploadImg} alt="upload" />
                  <input
                     type="file"
                     id="files"
                     onChange={(event) => {
                        setImageSelected(event.target.files[0]);
                     }}
                     className={classes.hidden}
                  />
                  <label htmlFor="files" className={classes.label}>
                     You can drag and drop or select from <span>here</span>
                  </label>
               </div>

               <div className={classes.cloudinaryButtonContainer}>
                  {imgSelected.name && (
                     <Button onClick={uploadToCloudiary} variant="contained">
                        Upload to Cloudinary
                     </Button>
                  )}
               </div>
            </div>
         </DialogContent>
         <DialogActions>
            <Button onClick={closeEventsForm}>Cancel</Button>
            <Button
               className={classes.button}
               onClick={onAddEvent}
               variant="contained"
               disabled={!formIsValid}
            >
               Save
            </Button>
         </DialogActions>
      </>
   );
};

export default FormFill;
